<script setup lang="ts">
import { computed, ref } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue';
import { $t } from '@/composables/translations/useTranslate.ts';

interface Props {
  open?: boolean;
  variant?: 'centered' | 'sidebar' | 'lightbox';
  position?: 'left' | 'right';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  title?: string;
  showCloseButton?: boolean;
  initialFocus?: HTMLElement | null;
  noPadding?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  variant: 'centered',
  position: 'right',
  size: 'md',
  title: '',
  showCloseButton: true,
  initialFocus: undefined,
  noPadding: false,
});

const emit = defineEmits<{
  'update:open': [value: boolean];
  close: [];
}>();

const panelRef = ref<HTMLElement | null>(null);
const staticAnimating = ref(false);

const closeModal = () => {
  emit('update:open', false);
  emit('close');
};

const sizeClasses = computed(() => {
  if (props.variant === 'sidebar') {
    const sidebarSizes = {
      sm: 'min-w-[400px] max-w-xs',
      md: 'min-w-[400px] max-w-md',
      lg: 'min-w-[400px] max-w-lg',
      xl: 'min-w-[400px] max-w-xl',
      full: 'min-w-[400px] max-w-full',
    };
    return sidebarSizes[props.size];
  }

  if (props.variant === 'centered') {
    const centeredSizes = {
      sm: 'max-w-sm',
      md: 'max-w-lg',
      lg: 'max-w-[680px]',
      xl: 'max-w-4xl',
      full: 'max-w-full',
    };
    return centeredSizes[props.size];
  }

  const lightboxSizes = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-[660px]',
    xl: 'max-w-4xl',
    full: 'max-w-full',
  };
  return lightboxSizes[props.size];
});

const panelClasses = computed(() => {
  const baseClasses = ['shadow-xl', 'relative', 'flex', 'flex-col', 'w-full', sizeClasses.value];

  if (props.variant === 'sidebar') {
    baseClasses.push('bg-primary');
  } else {
    baseClasses.push('bg-white');
  }

  if (props.variant === 'centered' || props.variant === 'lightbox') {
    baseClasses.push('mx-auto', 'max-h-[90vh]', 'overflow-hidden');
  } else {
    baseClasses.push('h-full', 'overflow-hidden');
  }

  if (staticAnimating.value) {
    baseClasses.push('animate-pulse');
  }

  return baseClasses.join(' ');
});

const backdropTransitionProps = {
  enter: 'duration-300 ease-out',
  'enter-from': 'opacity-0',
  'enter-to': 'opacity-100',
  leave: 'duration-200 ease-in',
  'leave-from': 'opacity-100',
  'leave-to': 'opacity-0',
};

const panelTransitionProps = computed(() => {
  if (props.variant === 'centered' || props.variant === 'lightbox') {
    return {
      enter: 'duration-300 ease-out',
      'enter-from': 'opacity-0 scale-95',
      'enter-to': 'opacity-100 scale-100',
      leave: 'duration-200 ease-in',
      'leave-from': 'opacity-100 scale-100',
      'leave-to': 'opacity-0 scale-95',
    };
  }

  const isLeft = props.position === 'left';
  return {
    enter: 'transform duration-300 ease-out',
    'enter-from': isLeft ? '-translate-x-full' : 'translate-x-full',
    'enter-to': 'translate-x-0',
    leave: 'transform duration-200 ease-in',
    'leave-from': 'translate-x-0',
    'leave-to': isLeft ? '-translate-x-full' : 'translate-x-full',
  };
});

const dialogContentClasses = computed(() => {
  const classes = ['fixed', 'inset-0', 'z-[65]'];
  if (props.variant === 'centered' || props.variant === 'lightbox') {
    classes.push(
      'overflow-y-auto',
      'flex',
      'items-center',
      'justify-center',
      'min-h-screen',
      'p-4',
      'sm:p-6'
    );
  } else {
    classes.push('overflow-hidden');
  }
  return classes.join(' ');
});

const panelWrapperClasses = computed(() => {
  if (props.variant === 'sidebar') {
    const classes = ['fixed', 'inset-y-0', 'flex', 'z-65'];
    if (props.position === 'left') {
      classes.push('left-0');
    } else {
      classes.push('right-0');
    }
    return classes.join(' ');
  }
  return '';
});

const contentPaddingClasses = computed(() => {
  if (props.noPadding) {
    return '';
  }
  return props.variant === 'sidebar' ? 'px-8 py-4' : 'px-12 pb-7';
});

const overflowClasses = computed(() => {
  return props.variant === 'lightbox' ? '' : 'overflow-y-auto';
});
</script>

<template>
  <TransitionRoot :show="open" as="template">
    <Dialog :open="open" :initial-focus="initialFocus" :static="true" @close="closeModal">
      <div :class="dialogContentClasses">
        <TransitionChild as="template" v-bind="backdropTransitionProps">
          <div class="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true"></div>
        </TransitionChild>
        <!-- Sidebar panel -->
        <div v-if="variant === 'sidebar'" :class="panelWrapperClasses">
          <TransitionChild as="template" v-bind="panelTransitionProps">
            <DialogPanel ref="panelRef" :class="panelClasses">
              <div class="px-8 pt-12 pb-4">
                <div class="flex items-center justify-between">
                  <DialogTitle
                    v-if="$slots.title || title"
                    class="text-xl font-semibold text-text-primary"
                  >
                    <slot name="title">{{ title }}</slot>
                  </DialogTitle>
                  <button
                    v-if="showCloseButton"
                    type="button"
                    class="text-gray-400 hover:text-gray-600 transition-colors w-6 h-6 flex items-center justify-center relative -top-1 -right-1"
                    :aria-label="$t('ibe.common.close', 'Close')"
                    @click="closeModal"
                  >
                    <i class="w-3 h-3 bg-black icon icon-x"></i>
                  </button>
                </div>
              </div>

              <div
                :class="[
                  'flex-1',
                  overflowClasses,
                  'scrollbar-thin',
                  'scrollbar-thumb-gray-300',
                  'scrollbar-track-gray-100',
                  contentPaddingClasses,
                ]"
              >
                <slot></slot>
              </div>

              <div v-if="$slots.footer" class="px-8 py-4 border-t border-gray-200 flex-shrink-0">
                <slot name="footer"></slot>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>

        <!-- Centered panel -->
        <TransitionChild v-else as="template" v-bind="panelTransitionProps">
          <DialogPanel ref="panelRef" :class="panelClasses">
            <div v-if="$slots.title || title || showCloseButton" class="px-12 pt-7 pb-5">
              <div class="flex items-start justify-between">
                <DialogTitle
                  v-if="$slots.title || title"
                  class="text-xl font-semibold text-text-primary"
                >
                  <slot name="title">{{ title }}</slot>
                </DialogTitle>
                <button
                  v-if="showCloseButton"
                  type="button"
                  class="text-gray-400 hover:text-gray-600 transition-colors w-6 h-6 flex items-center justify-center relative -top-1.5 -right-1.5"
                  :aria-label="$t('ibe.common.close', 'Close')"
                  @click="closeModal"
                >
                  <i class="w-3 h-3 bg-black icon icon-x"></i>
                </button>
              </div>
            </div>

            <div
              :class="[
                'flex-1',
                overflowClasses,
                'scrollbar-thin',
                'scrollbar-thumb-gray-300',
                'scrollbar-track-gray-100',
                contentPaddingClasses,
              ]"
            >
              <slot></slot>
            </div>

            <div v-if="$slots.footer" class="px-12 pb-7 flex-shrink-0">
              <slot name="footer"></slot>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
